import React from "react";
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

const productDetails = {
  'backup-now': {
    description: "UDS Backup Now bietet eine zuverlässige und sichere Backup-Lösung für Ihre wichtigen Daten. Nach der Bestellung analysieren wir Ihre Systemumgebung und berechnen den monatlichen Preis anhand der tatsächlich benötigten Backup-Dienste.",
    features: [
      "Endpoint File Sicherung",
      "Endpoint Image Sicherung Windows/Linux",
      "Windows Server System Setting Backup",
      "Hyper-V VM Backup",
      "VMware VM Backup",
      "Lokales MS Exchange Backup",
      "Office 365 Account Backup",
      "SQL Backup"
    ],
    pricing: `Die Preisberechnung erfolgt nach der Analyse Ihrer Systemumgebung basierend auf folgender Preisliste:

• Endpoint File Sicherung: 5€ pro Endpoint
• Endpoint Image Sicherung (Windows/Linux): 10€ pro Endpoint
• Windows Server System Setting Backup: 5€ pro Server
• Hyper-V VM Backup: 5€ pro VM
• VMware VM Backup: 7€ pro VM
• Lokales MS Exchange Backup: 2€ pro Account
• Office 365 Account Backup: 4€ pro Account
• SQL Backup: 3€ pro Datenbank

Die Abrechnung erfolgt monatlich basierend auf den tatsächlich genutzten Diensten.`,
    advantages: [
      "Individuelle Preisberechnung nach Bedarf",
      "Keine versteckten Kosten",
      "Flexible Anpassung der Services",
      "Professionelle Einrichtung",
      "24/7 Support inklusive"
    ]
  },
  'office-365': {
    description: "Microsoft Office 365, optimiert und unterstützt durch UDS-Solutions. Alle bekannten Office-Anwendungen plus Cloud-Services für maximale Produktivität.",
    features: [
      "Alle Office-Anwendungen",
      "Exchange Online",
      "SharePoint Online",
      "Teams",
      "OneDrive for Business"
    ],
    advantages: [
      "Technischer Support",
      "Migration-Service",
      "Benutzer-Schulungen",
      "Datensicherung",
      "Admin-Support"
    ]
  },
  'tsplus': {
    description: "Remote Desktop-Lösungen mit TSPlus für sicheren und effizienten Zugriff auf Ihre Arbeitsumgebung von überall.",
    features: [
      "HTML5 Client",
      "Sichere Verbindungen",
      "Load Balancing",
      "Drucker-Redirection",
      "Multi-Monitor Support"
    ],
    advantages: [
      "Kostengünstig",
      "Einfache Installation",
      "Hohe Performance",
      "Geringe Bandbreite",
      "Benutzerfreundlich"
    ]
  },
  'eset': {
    description: "ESET bietet zuverlässigen Schutz für Ihre IT-Infrastruktur. Wählen Sie aus drei Plänen den passenden Schutz für Ihr Unternehmen.",
    features: [
      "Plan 1: Basic Protection",
      "• Endpoint Antivirus und Antimalware",
      "• Firewall und Webschutz",
      "• Anti-Phishing",
      "• Zentrale Verwaltung",
      "",
      "Plan 2: Advanced Protection",
      "• Alles aus Plan 1",
      "• Server-Schutz",
      "• Verschlüsselung",
      "• Cloud Sandbox Analysis",
      "",
      "Plan 3: Enterprise Protection",
      "• Alles aus Plan 2",
      "• EDR (Endpoint Detection & Response)",
      "• Erweiterte Threat Intelligence",
      "• Premium Support 24/7",
      "• Security Awareness Training"
    ],
    advantages: [
      "Geringe Systemlast",
      "Hohe Erkennungsrate",
      "Echtzeit-Schutz",
      "Zentrale Verwaltung",
      "Regelmäßige Updates"
    ]
  },
  'lastpass': {
    description: "LastPass Enterprise bietet professionelles Passwort-Management für Ihr Unternehmen. Skalierbar und sicher für Teams jeder Größe.",
    features: [
      "Sicherer Passwort-Tresor",
      "Passwort-Generator",
      "Secure Sharing",
      "Multi-Faktor-Authentifizierung",
      "Single Sign-On (SSO)",
      "Detaillierte Sicherheitsberichte"
    ],
    advantages: [
      "DSGVO-konform",
      "24/7 Premium Support",
      "Security Dashboard",
      "Mobile Apps verfügbar",
      "Zentrale Verwaltung"
    ]
  },
  'nextcloud': {
    description: "Ihre private Cloud-Lösung mit Nextcloud. Sicher, flexibel und DSGVO-konform für Datenspeicherung und Zusammenarbeit.",
    features: [
      "File Sharing",
      "Kollaboration",
      "Kalender & Kontakte",
      "Video Meetings",
      "Mobile Apps"
    ],
    advantages: [
      "DSGVO-konform",
      "Volle Kontrolle",
      "On-Premise möglich",
      "Skalierbar",
      "Anpassbar"
    ]
  }
};

function About({ onNext, productType }) {
  const details = productDetails[productType];

  return (
    <SoftBox>
      <SoftBox width="80%" textAlign="center" mx="auto" mb={4}>
        <SoftTypography variant="body1" fontWeight="regular" color="text">
          {details.description}
        </SoftTypography>
      </SoftBox>

      <SoftBox mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <SoftTypography variant="h6" fontWeight="medium">
              Backup-Optionen & Preise
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              {details.features.map((feature, index) => (
                <React.Fragment key={index}>
                  • {feature}<br />
                </React.Fragment>
              ))}
            </SoftTypography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <SoftTypography variant="h6" fontWeight="medium">
              Vorteile
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              {details.advantages.map((advantage, index) => (
                <React.Fragment key={index}>
                  • {advantage}<br />
                </React.Fragment>
              ))}
            </SoftTypography>
          </Grid>

          {details.pricing && (
            <Grid item xs={12}>
              <SoftBox mt={2} p={3} border="1px solid #e9ecef" borderRadius="lg">
                <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                  Preisberechnung
                </SoftTypography>
                <SoftTypography variant="body2" color="text">
                  {details.pricing}
                </SoftTypography>
              </SoftBox>
            </Grid>
          )}
        </Grid>
      </SoftBox>

      <SoftBox mt={4} textAlign="center">
        <SoftButton color="info" onClick={onNext}>
          Jetzt konfigurieren
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

About.propTypes = {
  onNext: PropTypes.func.isRequired,
  productType: PropTypes.oneOf(['backup-now', 'office-365', 'tsplus', 'eset', 'lastpass', 'nextcloud']).isRequired,
};

export default About;